import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import { finalize, from, switchMap } from 'rxjs'
import * as yup from 'yup'

import { LoadingButton } from '@mui/lab'
import { Button, Grid, Typography } from '@mui/material'
import {
  AuthProfileType,
  ClientQueryParams,
  CreateContactPayload,
  DEMO_CLIENTS,
  DomainDetectionErrors,
  errorToString,
  getClientCode,
  getClientName,
  getClientPortalHost,
  getVendorCode,
  HTTPResponseStatus,
  IAccountType,
  RecaptchaActions,
  useSubjectSelector,
  useSubscriptionRef,
  validateDemoPortalEmailDomain,
  validFirstNameRegex,
  validLastNameRegex,
} from '@procom-labs/common'
import { TextHookField, useAlert } from '@procom-labs/molecules'

import { ScreenAlert } from '@auth-portal/components/alert'
import { Header } from '@auth-portal/components/header'
import { HmCreationForm } from '@auth-portal/components/hm-creation-form'
import { MicrosoftLoginButton } from '@auth-portal/components/ms-ss-login-button'
import { SocialMediaLogin } from '@auth-portal/components/social-media-login'
import { TermsAndConditions } from '@auth-portal/components/terms-and-conditions'
import { environment } from '@auth-portal/environment'
import {
  useAuthService,
  useIsMsMultiTenantClient,
  useIsUserCreationAllowed,
  useLoginTypes,
  useRecaptcha,
} from '@auth-portal/hooks'
import { authStore } from '@auth-portal/store'
import {
  FlowRequestType,
  LandingFormValues,
  SignUpRoutes,
  SocialLoginContext,
  UserExistResponse,
} from '@auth-portal/types'
import { getCallbackURL, getOriginBaseUrl } from '@auth-portal/util'
import { yupResolver } from '@hookform/resolvers/yup'

import { ProfileSelectionForm } from './profile-selection-form'

const PublicDomainNotAllowed = 'PublicDomainNotAllowed'

const FboMaintenance: React.FC = () => {
  const { t } = useTranslation('main')
  return (
    <div>
      <Header heading={t('form.completeRegistration.maintenanceTitle')} />
      <Typography component="p" variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {t('form.completeRegistration.maintenanceContent')}
      </Typography>
    </div>
  )
}

export const LandingForm: React.FC = () => {
  const { addAlert } = useAlert()
  const { getRecaptchaToken } = useRecaptcha()
  const navigate = useNavigate()
  const location = useLocation()
  const { t, i18n } = useTranslation('main')

  const authService = useAuthService()
  const { authConfigOptions } = useSubjectSelector(authStore, [
    'authConfigOptions',
  ])

  const [searchParams] = useSearchParams()
  const requestType = searchParams.get('requestType')
  const emailParam = searchParams.get('email')
  const migrationTokenParam = searchParams.get('migrationToken')
  const sourceParam = searchParams.get('source')
  const tenantParam = searchParams.get('tenant')
  const onboardingCodeParam = searchParams.get('onboardingCode')
  const isContractorInvite = searchParams.get('isContractorInvite')
  const isAnonymousShare = searchParams.get('isAnonymousShare')
  const anonymousToken = searchParams.get('token')
  const loginTypeParam = searchParams.get(ClientQueryParams.loginType)
  const isFboInvite = migrationTokenParam && sourceParam && tenantParam

  const isOtpSent = useRef<boolean>(false)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isDomainDetected, setIsDomainDetected] = useState<boolean>(false)
  const [isMultiSelect, setIsMultiSelect] = useState<boolean>(false)
  const [isFboMigrationTokenValid, setIsFboMigrationTokenValid] =
    useState<boolean>(false)

  const validateSubscriptionRef = useSubscriptionRef()
  const publicDomainSubscriptionRef = useSubscriptionRef()
  const userSubscriptionRef = useSubscriptionRef()
  const otpSubscriptionRef = useSubscriptionRef()
  const createSubscriptionRef = useSubscriptionRef()
  const fboInviteSubscriptionRef = useSubscriptionRef()

  const { loginType, siteConfig } = useSubjectSelector(authStore, [
    'loginType',
    'siteConfig',
  ])

  const { isContractorLogin, isClientLogin, isGorillaResumeLogin } =
    useLoginTypes()

  const getProfileType = useCallback((): AuthProfileType | string => {
    if (isContractorLogin) return AuthProfileType.Contractor
    if (isGorillaResumeLogin) return AuthProfileType.GorillaResumeUser
    return ''
  }, [isContractorLogin, isGorillaResumeLogin])

  const isUserCreationAllowed = useIsUserCreationAllowed()
  const isMSMultiTenantClient = useIsMsMultiTenantClient()
  const isProfileAllowedToEnterEmail = authConfigOptions?.isEmailLoginAllowed

  const shouldValidateEmailDomain = useMemo(() => {
    const clientName = getClientName()
    return DEMO_CLIENTS.includes(clientName)
  }, [])

  const validationSchema = useMemo(
    () =>
      yup.object({
        firstName: yup
          .string()
          .required(t('form.createAccount.firstNameError'))
          .matches(
            validFirstNameRegex,
            t('form.createAccount.lastNameValidation')
          ),
        lastName: yup
          .string()
          .required(t('form.createAccount.lastNameError'))
          .matches(
            validLastNameRegex,
            t('form.createAccount.lastNameValidation')
          ),
        email: yup
          .string()
          .test(
            'domain-validation',
            t('common.form.email.domainNotAllowed'),
            (value) => {
              if (isGorillaResumeLogin) {
                return true
              }
              return validateDemoPortalEmailDomain(
                value,
                siteConfig?.companyInternalEmailDomains,
                shouldValidateEmailDomain
              )
            }
          ),
      }),
    [t, shouldValidateEmailDomain, siteConfig, isGorillaResumeLogin]
  )

  const formInstance = useForm<LandingFormValues>({
    resolver: yupResolver(validationSchema),
    ...(shouldValidateEmailDomain
      ? {
          mode: 'onBlur',
          reValidateMode: 'onBlur',
        }
      : {}),
  })
  const { control, handleSubmit, setValue, formState } = formInstance

  const email = useWatch({ control, name: 'email' })
  const vendorCode = getVendorCode()

  const handleErrorMessages = useCallback(
    (errorMessage) => {
      addAlert({
        severity: 'error',
        message: errorMessage,
      })
    },
    [addAlert]
  )

  const handleSendOtp = useCallback(
    (userResponse: UserExistResponse | null, isNewUser: boolean = false) => {
      if (isOtpSent.current) return

      isOtpSent.current = true
      otpSubscriptionRef.current = from(
        getRecaptchaToken(RecaptchaActions.RequestResetPassword)
      )
        .pipe(
          switchMap((recaptcha) =>
            authService.generateSignInOtp(
              decodeURIComponent(email),
              recaptcha,
              i18n.language
            )
          ),
          finalize(() => setIsLoading(false))
        )
        .subscribe({
          next: () => {
            if (userResponse) {
              const user: UserExistResponse = {
                ...userResponse,
                profileType: loginType?.toLowerCase() ?? '',
                onboardingParam: onboardingCodeParam,
                migrationToken: migrationTokenParam,
                isFboMigrationTokenValid,
                tenantParam,
                isNewUser: isNewUser ? 'true' : 'false',
                firstLogin: isNewUser ? 'true' : 'false',
              }
              navigate(
                {
                  pathname: SignUpRoutes.ValidateSignIn,
                  search: `${createSearchParams({
                    email,
                  })}`,
                },
                {
                  state: user, // passing user info to login screen
                }
              )
            }
          },
          error: (error) => {
            isOtpSent.current = false
            handleErrorMessages(errorToString(error.message))
          },
        })
    },
    [
      email,
      navigate,
      loginType,
      getRecaptchaToken,
      i18n.language,
      otpSubscriptionRef,
      tenantParam,
      onboardingCodeParam,
      migrationTokenParam,
      isFboMigrationTokenValid,
      handleErrorMessages,
    ]
  )

  const handleDomainDetection = useCallback(
    (emailDomain: string) => {
      validateSubscriptionRef.current = from(
        getRecaptchaToken(RecaptchaActions.ValidateEmailDomain)
      )
        .pipe(
          switchMap((token) =>
            authService.handleCheckClientDomain(
              emailDomain,
              token,
              vendorCode,
              getClientCode()
            )
          ),
          finalize(() => setIsLoading(false))
        )
        .subscribe({
          next: (response) => {
            if (response) {
              setIsDomainDetected(true)
              // If it's not loginRedirect and doesn't have email in params already, then add it
              navigate(
                {
                  pathname: location.pathname,
                  search: !emailParam
                    ? `${createSearchParams({
                        email: emailDomain,
                      })}`
                    : location.search,
                },
                {
                  replace: false,
                }
              )
            } else {
              setIsMultiSelect(true)
            }
          },
          error: (error) => {
            let message = t('somethingWrong')
            const { data } = error.response
            if (
              data.message === DomainDetectionErrors.ClientCreateNotAllowed ||
              data.message === DomainDetectionErrors.ClientSignupNotAllowed
            ) {
              message = t('form.createAccount.errors.domainAccountsNotAllowed')
            }
            addAlert({
              message,
              severity: 'error',
            })
          },
        })
    },
    [
      t,
      navigate,
      emailParam,
      vendorCode,
      addAlert,
      location.pathname,
      location.search,
      getRecaptchaToken,
      validateSubscriptionRef,
    ]
  )

  const handlePublicDomainCheck = useCallback(
    (emailDomain: string) => {
      setIsLoading(true)
      publicDomainSubscriptionRef.current = from(
        getRecaptchaToken(RecaptchaActions.ValidateEmailDomain)
      )
        .pipe(
          switchMap((token) =>
            authService.handlePublicDomainCheck(emailDomain, token)
          )
        )
        .subscribe({
          next: (response) => {
            if (response) {
              addAlert({
                severity: 'error',
                message: t('form.createAccount.errors.publicDomainNotAllowed'),
              })
              setIsLoading(false)
            } else {
              handleDomainDetection(emailDomain)
            }
          },
          error: () => {
            // For some reason if public domain check fails, still proceed with client detection
            handleDomainDetection(emailDomain)
          },
        })
    },
    [
      t,
      addAlert,
      getRecaptchaToken,
      publicDomainSubscriptionRef,
      handleDomainDetection,
    ]
  )

  const handleUserVerification = useCallback(() => {
    if (shouldValidateEmailDomain && formState.errors.email) return
    if (email) {
      setIsLoading(true)
      userSubscriptionRef.current = from(
        getRecaptchaToken(RecaptchaActions.GetAnonymousUserInfo)
      )
        .pipe(
          switchMap((token) => authService.getIfUserExist(email, token)),
          finalize(() => setIsLoading(false))
        )
        .subscribe({
          next: (response) => {
            if (response) {
              handleSendOtp(response)
            }
          },
          error: (error) => {
            const { status } = error.response
            if (status === HTTPResponseStatus.NotFound) {
              if (isClientLogin) {
                // HM Signup Flow
                handlePublicDomainCheck(email)
              } else if (
                isUserCreationAllowed &&
                (isContractorLogin || isGorillaResumeLogin)
              ) {
                // Contractor | GorillaResume
                setIsMultiSelect(true)
              } else {
                addAlert({
                  severity: 'error',
                  message: t('form.alerts.accountDoesNotExist'),
                })
              }
            }
          },
        })
    }
  }, [
    t,
    email,
    addAlert,
    isClientLogin,
    isContractorLogin,
    isGorillaResumeLogin,
    handleSendOtp,
    getRecaptchaToken,
    userSubscriptionRef,
    isUserCreationAllowed,
    handlePublicDomainCheck,
    formState.errors.email,
    shouldValidateEmailDomain,
  ])

  const onSubmit = useCallback(
    (data: LandingFormValues) => {
      setIsLoading(true)
      const { firstName, lastName, phoneNumber, companyName, accountType } =
        data

      let payload: CreateContactPayload = {
        email: data.email ?? emailParam,
        lastName,
        firstName,
        phoneNumber: accountType === IAccountType.Contractor ? '' : phoneNumber,
        recaptchaToken: '', // Populated in pipe
        requestType: requestType ?? FlowRequestType.signup,
        language: i18n.language,
        vendorCode: getVendorCode(),
        callbackUrl: getCallbackURL(),
        originBase: getOriginBaseUrl(),
        authPortalBase: window.location.origin,
        clientCode: getClientCode(getClientPortalHost()) ?? '',
        profileType: getProfileType(),
      }

      createSubscriptionRef.current = from(
        getRecaptchaToken(RecaptchaActions.Signup)
      )
        .pipe(
          switchMap((token) => {
            payload = {
              ...payload,
              recaptchaToken: token,
              companyName:
                accountType === IAccountType.Client ? companyName : '',
            }
            return authService.createContactWithoutPassword(
              payload,
              accountType ?? IAccountType.Client
            )
          }),
          finalize(() => setIsLoading(false))
        )
        .subscribe({
          next: (response) => {
            // Contractor | GorillaResume
            if (accountType && accountType !== IAccountType.Client) {
              handleSendOtp(
                {
                  ...response,
                } as UserExistResponse,
                true
              )
            } else {
              navigate(
                {
                  pathname: SignUpRoutes.ValidateSignIn,
                  search: `${createSearchParams({
                    email,
                  })}`,
                },
                {
                  state: {
                    id: response.id,
                    firstName,
                    lastName,
                    phoneNumber,
                    profileType: loginType?.toLowerCase(),
                  } as UserExistResponse,
                }
              )
            }
          },
          error: (error) => {
            const { data: errData } = error.response
            let message = t('somethingWrong')

            if (errData.message === PublicDomainNotAllowed) {
              message = t('publicDomainsNotAllowed')
            } else if (
              errData.message ===
                DomainDetectionErrors.ClientCreateNotAllowed ||
              errData.message === DomainDetectionErrors.ClientSignupNotAllowed
            ) {
              message = t('form.createAccount.errors.domainAccountsNotAllowed')
            }

            addAlert({
              message,
              severity: 'error',
            })
          },
        })
    },
    [
      t,
      email,
      navigate,
      addAlert,
      emailParam,
      i18n.language,
      getRecaptchaToken,
      handleSendOtp,
      requestType,
      loginType,
      getProfileType,
      createSubscriptionRef,
    ]
  )

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        handleUserVerification()
      }
    },
    [handleUserVerification]
  )

  const handleFboInvite = useCallback(
    (migrationToken: string, source: string) => {
      if (migrationToken && source) {
        setIsLoading(true)
        fboInviteSubscriptionRef.current = authService
          .getUserMigrationInfo(migrationToken, source)
          .subscribe({
            next: (response: any) => {
              setIsLoading(false)
              const { data: migrationData } = response
              if (!migrationData.isMigrated) {
                // Throw error if the FBO user is not migrated yet
                handleErrorMessages(
                  t('fboUserInvitation.theUserIsNotMigratedYet')
                )
                setIsFboMigrationTokenValid(false)
              } else if (migrationData.isActivated) {
                // Throw error if the FBO user has been activated
                handleErrorMessages(
                  t('fboUserInvitation.theUserHasBeenActivated')
                )
                setIsFboMigrationTokenValid(false)
              } else {
                // Now the FBO migration info is valid.
                setIsFboMigrationTokenValid(true)
              }
            },
            error: (err: any) => {
              setIsLoading(false)
              handleErrorMessages(errorToString(err.response.data))
            },
          })
      }
    },
    [t, handleErrorMessages, fboInviteSubscriptionRef]
  )

  // OB + Contractor + GorillaResume
  useEffect(() => {
    if (emailParam) {
      setValue('email', emailParam)

      // Recruiter invite contractor
      // CS invite contractor
      // GorillaResume invite user
      if (isContractorInvite || onboardingCodeParam || isGorillaResumeLogin) {
        handleUserVerification()
      }
    }
  }, [
    emailParam,
    onboardingCodeParam,
    handleUserVerification,
    isContractorInvite,
    setValue,
    isGorillaResumeLogin,
  ])

  // FBO - Invitation
  useEffect(() => {
    const isFboInvitation = migrationTokenParam && sourceParam && tenantParam
    if (isFboInvitation) {
      handleFboInvite(migrationTokenParam, sourceParam)
    }
  }, [migrationTokenParam, handleFboInvite, sourceParam, tenantParam])

  // FBO - Handle token validated
  useEffect(() => {
    if (isFboMigrationTokenValid) {
      handleUserVerification()
    }
  }, [isFboMigrationTokenValid, handleUserVerification])

  useEffect(() => {
    if (isClientLogin) {
      setValue('accountType', IAccountType.Client)
    } else if (isContractorLogin) {
      setValue('accountType', IAccountType.Contractor)
    } else if (isGorillaResumeLogin) {
      setValue('accountType', IAccountType.GorillaResume)
    }
  }, [isClientLogin, isContractorLogin, isGorillaResumeLogin, setValue])

  // Note: this should not be necessary if all pages comes through initial-route first
  useEffect(() => {
    if (loginTypeParam && loginTypeParam.toLowerCase() !== loginType) {
      localStorage.setItem(ClientQueryParams.loginType, loginTypeParam)
      authService.setLoginType()
    }
  }, [loginTypeParam, loginType])

  // Anonymous share - Redirection
  useEffect(() => {
    if (isAnonymousShare && anonymousToken) {
      authService.redirectToClientPortal({
        isAnonymousShare: true,
        token: anonymousToken,
      })
    }
  }, [isAnonymousShare, anonymousToken])

  useEffect(() => {
    const subscription = authService
      .getSiteConfigurationByOriginBase()
      .subscribe()
    return () => {
      if (subscription && !subscription.closed) {
        subscription.unsubscribe()
      }
    }
  }, [])

  if (
    isFboInvite &&
    environment.IS_FBO_INVITATION_SIGNUP_MAINTENANCE_ENABLED === 'true'
  ) {
    return <FboMaintenance />
  }

  return (
    <FormProvider {...formInstance}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {!isDomainDetected && !isMultiSelect && (
          <Grid display="flex" flexDirection="column" rowGap={3}>
            <Header heading={t('form.contractorSignup.title')} />
            {isMSMultiTenantClient && (
              <MicrosoftLoginButton handleError={handleErrorMessages} />
            )}
            <ScreenAlert />

            <SocialMediaLogin context={SocialLoginContext.Login} />

            {isProfileAllowedToEnterEmail && (
              <Grid display="flex" flexDirection="column" rowGap={3}>
                <Grid item textAlign="left">
                  <TextHookField
                    control={control}
                    name="email"
                    type="email"
                    label={t('form.createAccount.enterWorkEmail')}
                    customLabel={false}
                    disabled={isLoading}
                    InputLabelProps={{
                      shrink: false,
                    }}
                    onKeyDown={handleKeyDown}
                  />
                </Grid>
                <Grid item>
                  <LoadingButton
                    variant="contained"
                    size="large"
                    color="secondary"
                    type="button"
                    fullWidth
                    disabled={!email}
                    loading={isLoading}
                    onClick={handleUserVerification}
                  >
                    {t('common.btn.submit')}
                  </LoadingButton>
                </Grid>

                <TermsAndConditions />
              </Grid>
            )}
          </Grid>
        )}

        {isDomainDetected && <HmCreationForm isLoading={isLoading} />}

        {isMultiSelect && <ProfileSelectionForm isLoading={isLoading} />}

        {(isDomainDetected || isMultiSelect) && (
          <Grid
            sx={{
              my: 2,
              px: {
                xs: 1,
                sm: 4,
                lg: 5,
                xl: 7,
                k2: 12.5,
              },
            }}
          >
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                setIsDomainDetected(false)
                setIsMultiSelect(false)
                navigate('/')
              }}
            >
              <Typography
                variant="body1Bold700"
                lineHeight={1.5}
                component="span"
              >
                {t('common.btn.back')}
              </Typography>
            </Button>
          </Grid>
        )}
      </form>
    </FormProvider>
  )
}
